import React, { useState } from 'react';
import { X, RefreshCcw, Copy, Rocket } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme/ThemeContext';

interface IntegrationModalProps {
  onClose: () => void;
}

const DeploymentModal: React.FC<IntegrationModalProps> = ({ onClose }) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const [deploymentType, setDeploymentType] = useState('Chatbot');
  const [workflow, setWorkflow] = useState('');
  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('sk-ABCD1234XYZ');
  const [token] = useState('token-XYZ-5678');
  const [status, setStatus] = useState('Active');

  const workflows = ['Task Manager Bot', 'Invoice Reporter', 'Lead Nurturing Agent'];

  const inputCls = isLight
    ? "w-full p-2 rounded bg-white border border-neutral-300 text-neutral-900 mt-1 text-sm focus:ring-1 focus:ring-indigo-500 outline-none"
    : "w-full p-2 rounded bg-neutral-800 text-white mt-1 text-sm outline-none";

  const labelCls = isLight ? "block text-neutral-500 mt-4 text-xs font-medium" : "block text-gray-400 mt-4 text-sm";
  const codeCls = isLight
    ? "w-full p-3 rounded bg-neutral-50 border border-neutral-200 text-indigo-700 text-xs font-mono"
    : "w-full p-3 rounded bg-neutral-900 text-green-400 text-xs";

  const regenerateApiKey = () => {
    setApiKey(`sk-${Math.random().toString(36).substring(2, 10).toUpperCase()}`);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const renderDynamicFields = () => {
    const commonFields = (
      <>
        <label className={labelCls}>Domain</label>
        <input
          type="text"
          placeholder="e.g., app.clientdomain.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className={inputCls}
        />

        <label className={labelCls}>Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={inputCls}
        >
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </>
    );

    switch (deploymentType) {
      case 'Chatbot':
        return (
          <>
            {commonFields}

            <label className={labelCls}>API Key</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={apiKey}
                className={isLight ? "w-full p-2 rounded bg-indigo-50 border border-indigo-100 text-indigo-700 mt-1 text-sm font-medium" : "w-full p-2 rounded bg-neutral-800 text-green-400 mt-1 text-sm"}
              />
              <button onClick={regenerateApiKey} className={`p-2 rounded mt-1 ${isLight ? 'bg-neutral-100 hover:bg-neutral-200 text-neutral-600' : 'bg-neutral-700 hover:bg-neutral-600'}`}>
                <RefreshCcw className="w-4 h-4" />
              </button>
            </div>

            <label className={labelCls}>Select Workflow</label>
            <select
              value={workflow}
              onChange={(e) => setWorkflow(e.target.value)}
              className={inputCls}
            >
              <option value="">Select Workflow</option>
              {workflows.map((wf) => (
                <option key={wf} value={wf}>{wf}</option>
              ))}
            </select>

            {workflow && domain && (
              <>
                <label className={labelCls}>Generated Embed Code</label>
                <div className="relative mt-1">
                  <textarea
                    rows={4}
                    readOnly
                    value={`<iframe src="https://${domain}/chatbot?workflow=${encodeURIComponent(workflow)}&apiKey=${apiKey}" width="100%" height="500px"></iframe>`}
                    className={codeCls}
                  />
                  <button
                    onClick={() => copyToClipboard(`<iframe src="https://${domain}/chatbot?workflow=${encodeURIComponent(workflow)}&apiKey=${apiKey}" width="100%" height="500px"></iframe>`)}
                    className={`absolute top-2 right-2 p-1.5 rounded ${isLight ? 'bg-white/80 hover:bg-white text-indigo-600 shadow-sm' : 'bg-neutral-700 hover:bg-neutral-600 text-gray-300'}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </>
        );

      case 'API Endpoint':
      case 'Webhook':
        const isApi = deploymentType === 'API Endpoint';
        const generatedUrl = isApi
          ? `POST https://${domain}/api/v1/workflows/trigger\n\nHeaders:\nAuthorization: Bearer ${token}\n\nBody:\n{\n  "workflow": "${workflow}"\n}`
          : `https://${domain}/webhook/trigger?token=${token}`;

        return (
          <>
            {commonFields}

            {domain && (
              <>
                <label className={labelCls}>
                  {isApi ? 'Generated API Endpoint' : 'Generated Webhook URL'}
                </label>
                <div className="relative mt-1">
                  <textarea
                    rows={isApi ? 6 : 3}
                    readOnly
                    value={generatedUrl}
                    className={codeCls}
                  />
                  <button
                    onClick={() => copyToClipboard(generatedUrl)}
                    className={`absolute top-2 right-2 p-1.5 rounded ${isLight ? 'bg-white/80 hover:bg-white text-indigo-600 shadow-sm' : 'bg-neutral-700 hover:bg-neutral-600 text-gray-300'}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </>
        );

      case 'Reporting':
        return (
          <p className={isLight ? "text-neutral-500 mt-4 text-sm" : "text-gray-400 mt-4 text-sm"}>
            Reporting links will be shared via email or chat notifications configured in the system workflows.
          </p>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50">
      <div className={`${isLight ? 'bg-white border border-neutral-200 shadow-2xl' : 'bg-neutral-900 border border-neutral-800 shadow-2xl'} p-8 rounded-2xl w-full max-w-lg relative`}>
        <button
          onClick={onClose}
          className={`absolute top-6 right-6 ${isLight ? 'text-neutral-400 hover:text-red-500' : 'text-gray-400 hover:text-white'} transition-colors`}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6 flex flex-col items-center">
          <div className={`p-3 rounded-full ${isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-900/30 text-indigo-400'} mb-3`}>
            <Rocket className="w-6 h-6" />
          </div>
          <h4 className={`text-lg font-bold ${isLight ? 'text-neutral-900' : 'text-gray-200'}`}>
            Create New Deployment
          </h4>
        </div>

        <label className={labelCls}>Deployment Type</label>
        <select
          value={deploymentType}
          onChange={(e) => setDeploymentType(e.target.value)}
          className={inputCls}
        >
          <option>Chatbot</option>
          <option>API Endpoint</option>
          <option>Reporting</option>
          <option>Webhook</option>
        </select>

        {renderDynamicFields()}

        <div className="mt-8">
          <button
            onClick={onClose}
            className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] transition-all px-5 py-2.5 rounded-lg text-white text-sm font-bold shadow-lg shadow-indigo-600/20"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};


export default DeploymentModal;
